import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Handshake, Globe, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';

const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPartners = useCallback(async () => {
    try {
      setError(null);
      const response = await axios.get(`${API}/partners`);
      setPartners(response.data || []);
    } catch (err) {
      setError('Impossible de charger les partenaires');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  if (error) {
    return (
      <div className="min-h-screen bg-black pt-24 flex items-center justify-center">
        <p className="font-['Barlow'] text-gray-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24">
      {/* Hero Section */}
      <section className="py-16 border-b border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-[0.3em] mb-4 block">
              Ils nous font confiance
            </span>
            <h1 className="font-['Teko'] text-5xl md:text-7xl text-white uppercase leading-none mb-6">
              NOS PARTENAIRES
            </h1>
            <p className="font-['Barlow'] text-gray-400 max-w-2xl mx-auto text-lg">
              Nous collaborons avec des professionnels de l'industrie du cinéma et de l'audiovisuel pour vous offrir les meilleures prestations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : partners.length === 0 ? (
            <div className="text-center py-20">
              <Handshake size={64} className="mx-auto text-gray-600 mb-6" />
              <p className="font-['Barlow'] text-gray-500 text-xl">
                Les partenaires seront bientôt disponibles.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {partners.map((partner, index) => (
                <motion.div
                  key={partner.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="group"
                >
                  <div className="bg-[#09090b] border border-white/10 hover:border-[#FACC15]/30 transition-all duration-300 p-6">
                    {/* Logo */}
                    <div className="aspect-square w-full mb-4 flex items-center justify-center bg-white/5 overflow-hidden">
                      {partner.logo_url ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}${partner.logo_url}`}
                          alt={partner.name}
                          className="w-full h-full object-contain p-4 transition-transform duration-300 group-hover:scale-110"
                        />
                      ) : (
                        <Handshake size={48} className="text-[#FACC15]" />
                      )}
                    </div>
                    
                    {/* Info */}
                    <h3 className="font-['Teko'] text-xl text-white text-center uppercase">
                      {partner.name}
                    </h3>
                    {partner.description && (
                      <p className="font-['Barlow'] text-gray-400 text-sm text-center mt-2 line-clamp-3">
                        {partner.description}
                      </p>
                    )}
                    {partner.website && (
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 mt-4 text-[#FACC15] text-sm hover:underline transition-colors"
                      >
                        <Globe size={14} />
                        Visiter le site
                        <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PartnersPage;
